import type { StepDraft } from '@shared/flow/enrollment';
import type { StagedStepCopy } from './state/staged-play-config';
import type { StepOverrideSnapshot } from './state/step-override-drafts';

/**
 * The pure logic behind the bulk step-override editor's LIVE model (plan
 * 2026-09-08-002 Part 2).
 *
 * v3 (this build) — the second rework. v1 was an explicit "Apply to N leads"
 * button (rejected in live QA for not matching the base-copy Bulk Editor's
 * live propagation, `.baton/2026-09-08-gong-bulk-step-defaults-fix.md`). v2
 * fixed the behavior but built a SECOND rail for it — a card-held sentinel
 * `createStepOverrideStore()` instance under item id `-1`, plus a card-level
 * send-trigger fan-out (`deriveBatchSendWrites`) wrapped around five send
 * entry points — next to the codebase's existing canonical rail for exactly
 * this concept (how the Bulk Editor's Base subject/body fields already
 * work): staged drafts in `state/staged-play-config.ts`, materialized once
 * inside the send pipeline's save phase. Devin's advisory review on PR #1390
 * (2026-09-09) flagged the duplication; Spencer adopted the fix in full.
 *
 * What v3 deletes with the sentinel store: its own sentinel item id constant,
 * defense-in-depth guard in `isBulkFollowEligible`, and
 * `deriveBatchSendWrites`/`BatchSendWrite` (the multi-item, multi-entry
 * send-trigger fan-out). What survives unchanged in substance: the
 * status allowlist, the fallback chain (`resolveEffectiveStepValues`, whose
 * `batchDraft` parameter narrows from a `{values}`-wrapped rung to a plain
 * `StepOverrideSnapshot | null` — a staged entry never carries a `null`
 * "reset" value, because Resolved Decision 8 makes a batch-level reset a
 * REMOVAL, not a `values: null` intent), and the own-draft-existence badge
 * (`isStepOverridden`).
 *
 * What v3 adds: `deriveStagedStepWrites` — the SINGLE-ITEM replacement for
 * `deriveBatchSendWrites`, called from inside
 * `EnrollQueueContext.saveStepOverrides`'s preamble (the one per-item write
 * moment every send path already converges on) rather than from N card-level
 * send wrappers. Same idempotent, stateless-derivation shape; narrower
 * because its caller is already per-item.
 *
 * U3 (gong-bulk-step-defaults-bugfix plan, MUST-ADDRESS) adds the
 * `liveSteps` reconciliation gate to `deriveStagedStepWrites`: a staged
 * step-copy entry for a step Gong-side deleted or renumbered upstream (the
 * batch editor only ever renders LIVE steps, so such an entry is invisible
 * there) used to still materialize into a PUT the send-time guard
 * (`enrollment-sender.ts`'s `assertLiveStepTypesMatchOverrides`) then
 * rejected, with no rep-visible way to clear it. A staged entry now only
 * materializes when a live step still exists at that `step_order` with the
 * SAME `step_type` — `null` (live steps could not be determined) fails safe
 * to zero writes, never to a blocked send.
 */

/**
 * The status allowlist a lead must pass to follow the batch default at all
 * (Key Technical Decision 6) — the lean-yes default carries over unchanged
 * from every prior version of this feature: a `sending`/`submitted` item's
 * displayed copy live-following keystrokes would misrepresent what that item
 * is actually sending. Written as a membership check over a `Set<string>`
 * rather than a `!== 'sending'` denylist SPECIFICALLY so the in-flight DBOS
 * durable-send migration's new `queued` status (its U1) is excluded the
 * moment it lands, with no edit to this file.
 */
export const BULK_FOLLOW_ELIGIBLE_STATUSES: ReadonlySet<string> = new Set(['unassigned', 'draft', 'ready']);

/**
 * The narrow slice of an enrollment item this module reads. Structural, not
 * `EnrollmentItem`, for the same reason `StepOverrideEditor` takes a plain
 * `itemId` (that file's header, spec §7 rule 4): this module is about ids,
 * statuses and persisted step rows, and nothing else. A real `EnrollmentItem`
 * off `group.items`/`cachedItem(itemId)` is assignable as-is — pinned by a
 * test — and `step_drafts` is `Pick`ed off the wire schema's own `StepDraft`
 * rather than re-declared, per `terra/no-parallel-schema-and-type`.
 *
 * `status` is `string`, NOT `EnrollmentItemStatus`: the allowlist's whole
 * purpose is to exclude a status this build has never heard of, which a
 * closed union would make untypeable (and untestable).
 */
export interface BulkFollowTargetItem {
  id: number;
  status: string;
  step_drafts: ReadonlyArray<Pick<StepDraft, 'step_number' | 'step_type' | 'subject_draft' | 'body_draft'>>;
}

/** See the module header. Key Technical Decision 6's one predicate — gates both the display rung and `deriveStagedStepWrites`. */
export function isBulkFollowEligible(item: BulkFollowTargetItem): boolean {
  return BULK_FOLLOW_ELIGIBLE_STATUSES.has(item.status);
}

/** One row's read of one step's own-draft-or-persisted state — the shape `StepOverrideRow` already threads through `localDraftFor`/`overrides`. */
export interface StepOverrideRung {
  values: StepOverrideSnapshot | null;
}

/**
 * Key Technical Decision 2 — the fallback chain's batch rung. Was
 * `localDraft ?? persistedOverride ?? stepDefault`; becomes `localDraft ??
 * persistedOverride ?? batchDraft ?? stepDefault`.
 *
 * Rung SELECTION, not a per-field merge across every rung: once `localDraft`
 * or `persistedOverride` is present, the batch rung is never consulted AT
 * ALL (Resolved Decision 3) — not even for an individual null field. Only
 * `persistedOverride`'s own two fields fall through per-field, to
 * `stepDefault`, exactly as they did before the batch rung existed (a
 * persisted non-email row's `subject_draft` is always `null` because
 * `saveStepOverrides` drops that key for those types).
 *
 * `localDraft.values === null` is the "use flow default" reset intent — it
 * resolves straight to `stepDefault`, never to `batchDraft`, per Resolved
 * Decision 2 ("a reset lead shows the flow default and does not rejoin the
 * batch").
 *
 * `batchDraft` is a plain `StepOverrideSnapshot | null` (v3) rather than the
 * `{values: StepOverrideSnapshot | null}`-wrapped rung v2 used: a staged
 * `stepCopy` entry (`state/staged-play-config.ts`) never carries a `null`
 * "reset" value — Resolved Decision 8 makes a batch-level reset a REMOVAL of
 * the staged entry, so there is nothing to unwrap. The caller
 * (`StepOverrideEditor.tsx`'s `StepOverrideRow`) adapts the staged
 * `{stepType, subject, body}` shape to `{subject_draft, body_draft}` before
 * calling this.
 */
export function resolveEffectiveStepValues({
  localDraft,
  persistedOverride,
  batchDraft,
  stepDefault,
}: {
  localDraft: StepOverrideRung | null;
  persistedOverride: Pick<StepDraft, 'subject_draft' | 'body_draft'> | undefined;
  batchDraft: StepOverrideSnapshot | null;
  stepDefault: StepOverrideSnapshot;
}): StepOverrideSnapshot {
  if (localDraft) return localDraft.values ?? stepDefault;
  if (persistedOverride) {
    return {
      subject_draft: persistedOverride.subject_draft ?? stepDefault.subject_draft,
      body_draft: persistedOverride.body_draft ?? stepDefault.body_draft,
    };
  }
  if (batchDraft) return batchDraft;
  return stepDefault;
}

/**
 * Key Technical Decision 3 — "Overridden" as own-draft EXISTENCE, not a
 * value diff against the flow default. Mirrors `isItemOverridden`'s shape
 * (`queue-group-send.ts:131-133`), the exact reason this shape is required
 * here too: once the batch rung exists, a row merely FOLLOWING the batch
 * default would otherwise show "Overridden" as well, since the batch text
 * will usually differ from the flow default.
 *
 * A local reset intent (`localDraft.values === null`) still counts as an own
 * draft — the badge (and "Use flow default" affordance) stays up, honestly
 * surfacing a pending DELETE, until Send actually processes it (Resolved
 * Decision 2 / Key Technical Decision 3).
 */
export function isStepOverridden({
  localDraft,
  persistedOverride,
}: {
  localDraft: StepOverrideRung | null;
  persistedOverride: Pick<StepDraft, 'subject_draft' | 'body_draft'> | undefined;
}): boolean {
  return localDraft !== null || persistedOverride !== undefined;
}

/** One write `deriveStagedStepWrites` plans — applied through `editStepOverride`, the ordinary local-draft write, never a network call directly. */
export interface StagedStepWrite {
  stepNumber: number;
  stepType: string;
  values: StepOverrideSnapshot;
}

/**
 * Key Technical Decision 5's send-time materialization — the
 * `deriveBatchSendWrites` replacement, SINGLE-ITEM because its one caller
 * (`EnrollQueueContext.saveStepOverrides`'s preamble) is already per-item:
 * every send path (group pipeline, row `handleSend`, the standalone
 * step-save path) converges on `saveStepOverrides(itemId)`, so there is no
 * multi-item fan-out left to do here.
 *
 * A write is planned for one staged step iff:
 * 1. the item passes `isBulkFollowEligible` (Key Technical Decision 6 — one
 *    predicate, two call sites: this one and the display gate),
 * 2. the item has no LOCAL draft for that step (own edit, including a reset
 *    intent — never overwritten), and
 * 3. the item has no PERSISTED `step_drafts` row for that step (an earlier
 *    session's own edit — also never overwritten).
 *
 * `stagedSteps` is `undefined` (nothing staged for this play) or an empty
 * record — both produce no writes, and `localDraftFor` is never even called
 * in that case (pinned by a test, mirroring `deriveBatchSendWrites`'s own
 * short-circuit).
 *
 * Idempotent by construction — re-deriving and re-writing the same values
 * costs nothing, which is what lets `saveStepOverrides` call this
 * unconditionally on every invocation rather than tracking whether it
 * already ran.
 */
export function deriveStagedStepWrites({
  item,
  stagedSteps,
  localDraftFor,
  liveSteps,
}: {
  /** The item `saveStepOverrides` is about to save — `cachedItem(itemId)`. */
  item: BulkFollowTargetItem;
  /** `readStagedConfig(stagedIdentity).stepCopy[playKey]` — this item's play's staged batch defaults, keyed `String(stepNumber)`. */
  stagedSteps: Readonly<Record<string, StagedStepCopy>> | undefined;
  /** `EnrollQueueContext.stepOverrideDraftFor` bound to this item's id — a stable, non-reactive `getState()` read. */
  localDraftFor: (stepNumber: number) => StepOverrideRung | null;
  /**
   * U3 — the flow's LIVE step list (a real `GET .../steps` read), or `null`
   * when it could not be determined (the `queryClient.fetchQuery` in
   * `EnrollQueueContext.saveStepOverrides` failed). `null` produces ZERO
   * staged writes — fail-safe: the send proceeds, the item's own drafts
   * still save, the batch default simply doesn't apply this time, matching
   * the batch editor's own "Flow steps unavailable" posture rather than
   * blocking the send.
   */
  liveSteps: ReadonlyArray<{ step_order: number; step_type: string }> | null;
}): StagedStepWrite[] {
  if (!stagedSteps || Object.keys(stagedSteps).length === 0) return [];
  if (!isBulkFollowEligible(item)) return [];

  const liveTypeByStepOrder = new Map((liveSteps ?? []).map(step => [step.step_order, step.step_type]));
  const persistedStepNumbers = new Set(item.step_drafts.map(row => row.step_number));
  const writes: StagedStepWrite[] = [];
  for (const [stepKey, staged] of Object.entries(stagedSteps)) {
    const stepNumber = Number(stepKey);
    if (localDraftFor(stepNumber) !== null) continue;
    if (persistedStepNumbers.has(stepNumber)) continue;
    // U3 — drop any staged entry whose step_order has no live step, or
    // whose live step_type no longer matches the staged one (deleted or
    // renumbered upstream). `liveSteps: null` makes every lookup miss.
    if (liveTypeByStepOrder.get(stepNumber) !== staged.stepType) continue;
    writes.push({
      stepNumber,
      stepType: staged.stepType,
      values: { subject_draft: staged.subject, body_draft: staged.body },
    });
  }
  return writes;
}
